import { faChevronDown, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth.store";

interface FormData {
	email: string;
	roleId: string;
}

interface Role {
	id: string;
	name: string;
}

export interface AddInviteFormProps {
	formData: FormData;
	setFormData: (data: FormData) => void;
	isEditing: boolean;
	id?: string;
	onSubmit: (formData: FormData) => void;
}

const AddInviteForm: React.FC<AddInviteFormProps> = ({
	formData,
	setFormData,
	isEditing,
	onSubmit
}) => {
	const { brandId } = useAuthStore();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { data: roles = [] } = useQuery<Role[]>({
		queryKey: [`brand-roles-${brandId}`],
		queryFn: async () => {
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["BRAND", "ROLES", "LIST"], brandId)
			);
			return data;
		}
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.email || !formData.roleId) {
			toast.error("Please fill in all fields");
			return;
		}

		setIsSubmitting(true);
		onSubmit(formData);
	};

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	return (
		<div className="h-full">
			<form
				className="bg-white flex flex-col h-full max-w-md mx-auto rounded-lg"
				onSubmit={handleSubmit}
			>
				<div className="mb-4 flex-1">
					{!isEditing && (
						<div className="mb-4">
							<label
								htmlFor="email"
								className="block text-sm font-medium text-gray-700 mb-1"
							>
								Email
							</label>
							<input
								name="email"
								type="email"
								placeholder="Enter email"
								value={formData.email}
								onChange={handleInputChange}
								className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								disabled={isSubmitting}
							/>
						</div>
					)}
					<div className="mb-4">
						<label
							htmlFor="roleId"
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							Role
						</label>
						<div className="relative w-full">
							<select
								name="roleId"
								value={formData.roleId}
								onChange={handleInputChange}
								className="appearance-none w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								disabled={isSubmitting}
							>
								<option value="">Select a role</option>
								{roles.map((role) => (
									<option key={role.id} value={role.id}>
										{role.name}
									</option>
								))}
							</select>
							<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
								<FontAwesomeIcon
									icon={faChevronDown}
									className="text-gray-500 text-sm"
								/>
							</div>
						</div>
					</div>
				</div>
				<div className="py-4 bg-white mb-[15px]">
					<button
						type="submit"
						className="w-full bg-blue-fynxt text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex justify-center items-center"
						disabled={isSubmitting}
					>
						{isSubmitting ? (
							<>
								<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
								{isEditing ? "Updating..." : "Inviting..."}
							</>
						) : isEditing ? (
							"Update User"
						) : (
							"Invite User"
						)}
					</button>
				</div>
			</form>
		</div>
	);
};

export default AddInviteForm;
